import React from "react";
import {ShoppingCart, Trash2, X} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {useCart} from "./CardContext";

const CartItem = ({item, onCheckout, onRemove}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onCheckout(item)}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onCheckout(item);
      }
    }}
    className="flex cursor-pointer gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-colors hover:border-blue-200 hover:bg-blue-50/40"
  >
    <img
      src={item.thumbnail || "/images/course_placeholder.jpg"}
      alt={item.title}
      className="h-20 w-24 shrink-0 rounded-md object-cover"
      loading="lazy"
    />
    <div className="min-w-0 flex-1">
      <p className="line-clamp-2 text-sm font-semibold text-slate-900">{item.title}</p>
      <p className="mt-1 truncate text-xs text-slate-500">{item.instructor}</p>
      <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
        {item.category && <span>{item.category}</span>}
        {item.duration && <span>{item.duration}</span>}
        {item.level && <span>{item.level}</span>}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onCheckout(item);
          }}
          className="rounded-md bg-blue-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-800"
        >
          Checkout
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(item);
          }}
          aria-label="Remove from cart"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
);

const CartModal = () => {
  const {cart, isCartOpen, closeCart, removeFromCart} = useCart();
  const navigate = useNavigate();

  if (!isCartOpen) {
    return null;
  }

  const checkout = (item) => {
    closeCart();
    navigate(`/cart?checkoutId=${encodeURIComponent(item.id)}&checkoutType=${encodeURIComponent(item.type || "course")}`);
  };

  return (
    <div className="fixed inset-0 z-[99999] flex justify-end bg-slate-950/50 backdrop-blur-sm">
      <button
        type="button"
        aria-label="Close cart"
        className="absolute inset-0 cursor-default"
        onClick={closeCart}
      />
      <aside className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-blue-900" />
            <div>
              <h2 className="text-lg font-bold text-slate-900">Cart</h2>
              <p className="text-xs text-slate-500">{cart.length} item{cart.length === 1 ? "" : "s"}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeCart}
            aria-label="Close cart"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {cart.length > 0 ? (
            <div className="space-y-3">
              {cart.map((item) => (
                <CartItem
                  key={`${item.type || "course"}-${item.id}`}
                  item={item}
                  onCheckout={checkout}
                  onRemove={(cartItem) => removeFromCart(cartItem.id, cartItem.type)}
                />
              ))}
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <ShoppingCart className="h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">Your cart is empty</p>
              <p className="mt-1 text-xs text-slate-500">Add books, courses, or research materials to get started.</p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 p-5">
          <button
            type="button"
            onClick={() => {
              closeCart();
              if (cart.length > 0) {
                navigate("/cart");
              }
            }}
            disabled={cart.length === 0}
            className="w-full rounded-md border border-blue-900 px-4 py-2.5 text-sm font-semibold text-blue-900 transition-colors hover:bg-blue-50"
          >
            Open Full Cart
          </button>
        </div>
      </aside>
    </div>
  );
};

export default CartModal;
